import { useCallback, useMemo, useRef, useState } from 'react'
import type { GridLeafColumn } from './useGridState'

/** Columns rendered beyond each edge, so a scroll reveals a filled cell. */
const BUFFER_COLUMNS = 3
/**
 * Below this many scrollable columns, rendering them all costs less than the
 * bookkeeping — a six-column grid never scrolls far enough to pay off.
 */
const MIN_COLUMNS_TO_VIRTUALIZE = 12

export interface GridColumnWindow<T> {
  /** Pinned columns plus the scrollable slice currently in view. */
  columns: GridLeafColumn<T>[]
  /** Width of the scrollable columns skipped before the slice. */
  leadSpacer: number
  /** Width of the scrollable columns skipped after it. */
  trailSpacer: number
}

/**
 * Horizontal virtualization. A wide grid (Station Setup runs ~30 columns)
 * otherwise builds every cell of every row, so the column count multiplies the
 * cost of each row that scrolls into view. Pinned columns always render —
 * they are sticky and must stay put — and the skipped scrollable columns are
 * replaced by two spacer widths, so the row keeps its full geometry.
 */
const useColumnWindow = <T,>(
  leafColumns: GridLeafColumn<T>[],
  viewportWidth: number,
): { window: GridColumnWindow<T>; onScrollLeft: (scrollLeft: number) => void } => {
  const [range, setRange] = useState<{ start: number; end: number } | null>(null)
  const scrollLeftRef = useRef(0)

  const layout = useMemo(() => {
    const middle: { leaf: GridLeafColumn<T>; start: number; end: number }[] = []
    let pinnedLeftWidth = 0
    let pinnedRightWidth = 0
    let x = 0

    leafColumns.forEach((leaf) => {
      if (leaf.pinned === 'left') pinnedLeftWidth += leaf.width
      else if (leaf.pinned === 'right') pinnedRightWidth += leaf.width
      else {
        middle.push({ leaf, start: x, end: x + leaf.width })
        x += leaf.width
      }
    })

    return { middle, pinnedLeftWidth, pinnedRightWidth }
  }, [leafColumns])

  const enabled = layout.middle.length >= MIN_COLUMNS_TO_VIRTUALIZE && viewportWidth > 0

  const computeRange = useCallback(
    (scrollLeft: number) => {
      const { middle, pinnedLeftWidth, pinnedRightWidth } = layout
      // Content coordinates: the sticky pinned columns cover the viewport's
      // outer edges, so the scrollable band visible between them is what the
      // middle columns are measured against.
      const from = scrollLeft
      const to = scrollLeft + Math.max(0, viewportWidth - pinnedLeftWidth - pinnedRightWidth)

      let start = middle.findIndex((entry) => entry.end > from)
      if (start < 0) start = middle.length - 1
      let end = middle.findIndex((entry) => entry.start >= to)
      if (end < 0) end = middle.length
      else end += 1

      return {
        start: Math.max(0, start - BUFFER_COLUMNS),
        end: Math.min(middle.length, end + BUFFER_COLUMNS),
      }
    },
    [layout, viewportWidth],
  )

  const onScrollLeft = useCallback(
    (scrollLeft: number) => {
      scrollLeftRef.current = scrollLeft
      if (!enabled) return
      const next = computeRange(scrollLeft)
      // Re-render only when the rendered slice actually changes, not on every
      // pixel of the scroll.
      setRange((prev) =>
        prev && prev.start === next.start && prev.end === next.end ? prev : next,
      )
    },
    [enabled, computeRange],
  )

  const window = useMemo<GridColumnWindow<T>>(() => {
    if (!enabled) return { columns: leafColumns, leadSpacer: 0, trailSpacer: 0 }

    const { middle } = layout
    const { start, end } = range ?? computeRange(scrollLeftRef.current)

    const leadSpacer = middle
      .slice(0, start)
      .reduce((sum, entry) => sum + entry.leaf.width, 0)
    const trailSpacer = middle
      .slice(end)
      .reduce((sum, entry) => sum + entry.leaf.width, 0)

    return {
      columns: [
        ...leafColumns.filter((leaf) => leaf.pinned === 'left'),
        ...middle.slice(start, end).map((entry) => entry.leaf),
        ...leafColumns.filter((leaf) => leaf.pinned === 'right'),
      ],
      leadSpacer,
      trailSpacer,
    }
  }, [enabled, leafColumns, layout, range, computeRange])

  return { window, onScrollLeft }
}

export default useColumnWindow

/**
 * Index of the first and last scrollable column in a rendered slice. The
 * spacers that stand in for virtualized-away columns belong between the pinned
 * edges and the slice — a spacer rendered outside them would push the sticky
 * pinned cells out of their flow position and unpin them at the far edges.
 */
export const middleBounds = <T,>(
  leafColumns: GridLeafColumn<T>[],
): { first: number; last: number } => {
  let first = -1
  let last = -1
  leafColumns.forEach((leaf, index) => {
    if (leaf.pinned) return
    if (first < 0) first = index
    last = index
  })
  return { first, last }
}
